import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarIcon } from "lucide-react";
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@sportscanner/ui";
import { enUS, ru, srLatn } from "react-day-picker/locale";
import type { Locale, Matcher } from "react-day-picker";
import { bcp47Locale, dateStrToEntry } from "./helpers";

interface Props {
  /** YYYY-MM-DD or empty string. */
  value: string;
  onChange: (next: string) => void;
  min?: string; // YYYY-MM-DD inclusive
  max?: string; // YYYY-MM-DD inclusive
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
}

// Native <input type="date"> takes its display language from the OS, not from
// `lang` (Chrome and Safari ignore it). Custom picker built on Popover +
// react-day-picker is the only way to honour the in-app language.
export default function DatePicker({
  value,
  onChange,
  min,
  max,
  disabled,
  placeholder,
  className,
  id,
}: Props) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);

  const locale: Locale = useMemo(() => {
    if (i18n.language === "sr") return srLatn;
    if (i18n.language === "ru") return ru;
    return enUS;
  }, [i18n.language]);

  const intlLocale = bcp47Locale(i18n.language);
  const intlFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(intlLocale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    [intlLocale],
  );

  const selectedDate = useMemo(() => parseDate(value), [value]);
  const minDate = useMemo(() => parseDate(min), [min]);
  const maxDate = useMemo(() => parseDate(max), [max]);

  // Combine min/max guards into a Matcher list react-day-picker accepts.
  const disabledMatcher = useMemo<Matcher[] | undefined>(() => {
    const matchers: Matcher[] = [];
    if (minDate) matchers.push({ before: minDate });
    if (maxDate) matchers.push({ after: maxDate });
    return matchers.length ? matchers : undefined;
  }, [minDate, maxDate]);

  const displayLabel = value
    ? intlFormat.format(selectedDate ?? new Date())
    : (placeholder ?? t("common.selectDate"));

  // Optional secondary line — matches the "Monday, 31. May" pattern used
  // elsewhere on the admin so admins instantly recognise the weekday.
  const subLabel = useMemo(() => {
    if (!value) return "";
    const e = dateStrToEntry(value);
    return `${t(`days.long.${e.dayKey}`)}`;
  }, [value, t]);

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={`w-full justify-between font-normal cursor-pointer ${className ?? ""}`}
        >
          <span className="flex items-center gap-2 truncate">
            <CalendarIcon className="w-4 h-4 text-neutral-500 shrink-0" />
            <span className="truncate">
              {value && subLabel ? (
                <>
                  <span className="text-neutral-500">{subLabel},</span>{" "}
                  <span>{displayLabel}</span>
                </>
              ) : (
                <span className={value ? "" : "text-neutral-400"}>
                  {displayLabel}
                </span>
              )}
            </span>
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={locale}
          weekStartsOn={1}
          selected={selectedDate ?? undefined}
          onSelect={(d) => {
            if (!d) return;
            onChange(toDateStr(d));
            setOpen(false);
          }}
          defaultMonth={selectedDate ?? minDate ?? undefined}
          disabled={disabledMatcher}
        />
      </PopoverContent>
    </Popover>
  );
}

function parseDate(s: string | undefined): Date | null {
  if (!s) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
